<template>
  <div class="container-fluid p-4 shadow-sm rounded" :class="themeClass">

    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0 fw-bold">Nota de Venta</h5>
      <button class="btn btn-outline-secondary btn-sm" @click="$router.push('/ventas/documentos-emitidos')">
        <i class="bi bi-arrow-left me-1"></i>Volver
      </button>
    </div>

    <div class="alert alert-secondary d-flex align-items-center gap-2 mb-3 py-2">
      <i class="bi bi-info-circle-fill"></i>
      <div>Documento sin validez tributaria: no se firma ni se envía al SRI. Se cobra de inmediato y descuenta inventario.</div>
    </div>

    <div v-if="alerta.mensaje" :class="['alert', 'd-flex', 'align-items-center', 'gap-2', 'mb-3', claseAlerta]">
      <i class="bi bi-exclamation-triangle-fill"></i>
      <div class="flex-grow-1">{{ alerta.mensaje }}</div>
      <button type="button" class="btn-close" @click="alerta.mensaje = ''"></button>
    </div>

    <!-- DATOS DEL COMPRADOR -->
    <div class="card mb-4">
      <div class="card-header bg-primary text-white fw-bold py-1">
        Datos del Comprador
      </div>
      <div class="card-body py-2">
        <div class="row gy-2">
          <div class="col-md-3">
            <label class="form-label small mb-1">Tipo Identificación</label>
            <select class="form-select form-select-sm" v-model="buyer.tipoIdentificacion" @change="onCambioTipoIdentificacion">
              <option value="">Seleccione</option>
              <option v-for="type in sriTypes" :key="type.code" :value="type.code">{{ type.name }}</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label small mb-1">Identificación</label>
            <div class="input-group input-group-sm">
              <input
                type="text"
                class="form-control form-control-sm"
                placeholder="RUC / Cédula / Pasaporte"
                v-model="buyer.identificacion"
                @blur="buscarClienteAutomatico('identificacion')"
                @keyup.enter="buscarClienteAutomatico('identificacion')"
              />
              <span v-if="buscandoCliente" class="input-group-text">
                <span class="spinner-border spinner-border-sm" role="status"></span>
              </span>
            </div>
          </div>
          <div class="col-md-6">
            <label class="form-label small mb-1">Nombres / Razón Social</label>
            <div class="position-relative">
              <div class="input-group input-group-sm">
                <input
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Ingrese nombre o razón social"
                  v-model="buyer.razonSocial"
                  @input="buscarClientesMientrasEscribe"
                  @blur="cerrarSugerenciasConDelay"
                  @keydown.down.prevent="navegarSugerencia(1)"
                  @keydown.up.prevent="navegarSugerencia(-1)"
                  @keydown.enter.prevent="seleccionarSugerenciaActiva"
                  @keydown.escape="cerrarSugerencias"
                  autocomplete="off"
                />
                <span v-if="buscandoCliente" class="input-group-text">
                  <span class="spinner-border spinner-border-sm" role="status"></span>
                </span>
              </div>
              <ul v-if="sugerenciasClientes.length > 0 && mostrarSugerencias" class="list-group position-absolute w-100 shadow-sm sugerencias-lista">
                <li
                  v-for="(cliente, index) in sugerenciasClientes"
                  :key="cliente.id"
                  class="list-group-item list-group-item-action py-1 px-2 small"
                  :class="{ active: index === indiceSugerenciaActiva }"
                  @mousedown.prevent="seleccionarCliente(cliente)"
                >
                  <div class="fw-semibold">{{ cliente.business_name }}</div>
                  <small class="text-muted">{{ cliente.identification_number }}</small>
                </li>
              </ul>
            </div>
          </div>
          <div class="col-md-4">
            <label class="form-label small mb-1">Correo Electrónico</label>
            <input type="email" class="form-control form-control-sm" placeholder="correo@ejemplo.com" v-model="buyer.correo" />
          </div>
          <div class="col-md-4">
            <label class="form-label small mb-1">Teléfono</label>
            <input type="text" class="form-control form-control-sm" v-model="buyer.telefono" />
          </div>
          <div class="col-md-4">
            <label class="form-label small mb-1">Dirección</label>
            <input type="text" class="form-control form-control-sm" v-model="buyer.direccion" />
          </div>
        </div>
      </div>
    </div>

    <!-- PRODUCTOS -->
    <div class="card mb-4">
      <div class="card-header bg-primary text-white fw-bold py-1">
        Productos
      </div>
      <div class="card-body py-2">
        <div class="row gy-2 mb-3">
          <div class="col-12 position-relative">
            <label class="form-label small mb-1">Buscar Producto</label>
            <input
              type="text"
              class="form-control form-control-sm"
              placeholder="Escribe código, nombre o código de barras..."
              v-model="busquedaProducto"
              @input="onBusquedaProductoInput"
              @keyup.enter.prevent="buscarProductoInmediato"
            />
            <div v-if="buscandoProducto" class="mt-1">
              <div class="spinner-border spinner-border-sm" role="status"></div>
            </div>
            <ul v-if="sugerenciasProducto.length" class="list-group position-absolute w-100 shadow-sm sugerencias-lista" style="max-height: 220px; overflow-y: auto;">
              <li
                v-for="p in sugerenciasProducto"
                :key="p.id"
                class="list-group-item list-group-item-action small d-flex justify-content-between align-items-center"
                :class="{ disabled: !esServicio(p) && parseFloat(p.stock_total || 0) <= 0 }"
                @mousedown.prevent="agregarProducto(p)"
              >
                <span><strong>{{ p.codigo_principal }}</strong> - {{ p.nombre_producto }}</span>
                <span v-if="!esServicio(p)" class="badge" :class="parseFloat(p.stock_total || 0) > 0 ? 'bg-success' : 'bg-danger'">
                  {{ parseFloat(p.stock_total || 0) <= 0 ? 'Sin stock' : 'Stock: ' + parseFloat(p.stock_total || 0).toFixed(0) }}
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table table-bordered table-sm align-middle">
            <thead class="table-light text-center small">
              <tr>
                <th>#</th>
                <th>CÓDIGO</th>
                <th>NOMBRE</th>
                <th>CANTIDAD</th>
                <th>PRECIO</th>
                <th>DESC. $</th>
                <th>SUBTOTAL</th>
                <th>% IVA</th>
                <th>IVA</th>
                <th>TOTAL</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="lineas.length === 0">
                <td colspan="11" class="text-center text-muted">Sin productos agregados</td>
              </tr>
              <tr v-for="(linea, idx) in lineas" :key="idx" :class="{ 'table-warning': linea.producto_id && !linea.es_servicio && linea.cantidad > linea.stock_disponible }">
                <td class="text-center">{{ idx + 1 }}</td>
                <td>{{ linea.codigo }}</td>
                <td>{{ linea.descripcion }}</td>
                <td>
                  <input
                    type="number"
                    class="form-control form-control-sm text-center"
                    v-model.number="linea.cantidad"
                    min="1" step="1"
                    :max="(linea.producto_id && !linea.es_servicio) ? linea.stock_disponible : undefined"
                    @change="ajustarCantidad(linea)"
                  />
                </td>
                <td>
                  <input type="number" class="form-control form-control-sm text-end" v-model.number="linea.precio_unitario" min="0" step="0.0001" @input="calcularLinea(linea)" />
                </td>
                <td>
                  <input type="number" class="form-control form-control-sm text-end" v-model.number="linea.descuento" min="0" step="0.01" @input="calcularLinea(linea)" />
                </td>
                <td class="text-end">{{ parseFloat(linea.subtotal || 0).toFixed(2) }}</td>
                <td class="text-center">
                  <select class="form-select form-select-sm" v-model.number="linea.porcentaje_iva" @change="calcularLinea(linea)">
                    <option :value="0">0%</option>
                    <option :value="5">5%</option>
                    <option :value="15">15%</option>
                  </select>
                </td>
                <td class="text-end">{{ parseFloat(linea.iva || 0).toFixed(2) }}</td>
                <td class="text-end fw-semibold">{{ parseFloat(linea.total_detalle || 0).toFixed(2) }}</td>
                <td class="text-center">
                  <button class="btn btn-danger btn-sm" @click="eliminarLinea(idx)"><i class="bi bi-trash"></i></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="lineasConStockExcedido > 0" class="alert alert-warning py-2 small mb-0">
          <i class="bi bi-exclamation-triangle-fill me-2"></i>
          {{ lineasConStockExcedido }} línea(s) superan el stock disponible. No es posible guardar hasta corregirlas.
        </div>
      </div>
    </div>

    <!-- COBRO Y TOTALES -->
    <div class="row mb-4 gy-3">
      <div class="col-md-6 d-flex flex-column gap-3">
        <!-- NOTAS -->
        <div class="card">
          <div class="card-header bg-primary text-white fw-bold py-1">Notas</div>
          <div class="card-body py-2">
            <textarea class="form-control form-control-sm" rows="3" placeholder="Observaciones..." v-model="notas"></textarea>
          </div>
        </div>

        <!-- COBRO -->
        <div class="card">
          <div class="card-header bg-primary text-white fw-bold py-1">Cobro</div>
          <div class="card-body py-2">
            <div class="row gy-2">
              <div class="col-md-6">
                <label class="form-label small mb-1">Método de Pago</label>
                <select class="form-select form-select-sm" v-model="cobro.metodo_pago">
                  <option value="efectivo">Efectivo</option>
                  <option value="tarjeta">Tarjeta</option>
                  <option value="transferencia">Transferencia</option>
                  <option value="cheque">Cheque</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label small mb-1">Destino del Cobro</label>
                <select class="form-select form-select-sm" v-model="cobro.destino_tipo" @change="cobro.destino_id = ''">
                  <option value="caja">Caja</option>
                  <option value="banco">Banco</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label small mb-1">{{ cobro.destino_tipo === 'caja' ? 'Caja (con sesión abierta)' : 'Banco' }}</label>
                <select class="form-select form-select-sm" v-model="cobro.destino_id">
                  <option value="">Seleccione...</option>
                  <option v-for="d in destinosDisponibles" :key="d.id" :value="d.id">{{ d.nombre }}</option>
                </select>
                <small v-if="cobro.destino_tipo === 'caja' && destinosDisponibles.length === 0" class="text-danger d-block mt-1">
                  No hay cajas con sesión abierta.
                </small>
              </div>
              <div v-if="cobro.destino_tipo === 'banco'" class="col-md-6">
                <label class="form-label small mb-1">N° Comprobante (opcional)</label>
                <input type="text" class="form-control form-control-sm" v-model="cobro.numero_comprobante" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-6">
        <div class="card h-100">
          <div class="card-header bg-primary text-white fw-bold py-1">Totales</div>
          <div class="card-body py-2">
            <ul class="list-group list-group-flush small">
              <li class="list-group-item py-1 d-flex justify-content-between">
                Subtotal <span class="fw-semibold">${{ subtotal.toFixed(2) }}</span>
              </li>
              <li v-if="descuentoTotal > 0" class="list-group-item py-1 d-flex justify-content-between">
                Descuento <span class="fw-semibold text-danger">-${{ descuentoTotal.toFixed(2) }}</span>
              </li>
              <li v-for="g in gruposIva" :key="g.porcentaje" class="list-group-item py-1 d-flex justify-content-between">
                IVA {{ g.porcentaje }}% <span class="fw-semibold">${{ g.iva.toFixed(2) }}</span>
              </li>
              <li class="list-group-item py-2 d-flex justify-content-between fw-bold fs-6 border-top">
                VALOR TOTAL <span>${{ total.toFixed(2) }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- BOTONES -->
    <div class="mt-3 d-flex gap-3 justify-content-end">
      <button class="btn btn-outline-secondary btn-sm px-3" @click="$router.push('/ventas/documentos-emitidos')">
        Cancelar
      </button>
      <button class="btn btn-success btn-sm px-4" @click="guardar" :disabled="guardando || lineas.length === 0">
        <span v-if="guardando" class="spinner-border spinner-border-sm me-1" role="status"></span>
        Guardar y Cobrar
      </button>
    </div>

  </div>
</template>

<script>
import api from '@/services/api'
import themeManager from '@/store/theme'
import { notaVentaService } from '@/services/notaVentaService'
import notificaciones from '@/services/notificationService.js'

export default {
  name: 'ClientNotaVentaNueva',

  data() {
    return {
      guardando: false,
      alerta: { mensaje: '', tipo: 'error' },

      sriTypes: [],
      buyer: {
        clientId: null,
        tipoIdentificacion: '',
        identificacion: '',
        razonSocial: '',
        correo: '',
        telefono: '',
        direccion: ''
      },
      buscandoCliente: false,
      sugerenciasClientes: [],
      mostrarSugerencias: false,
      indiceSugerenciaActiva: -1,
      debounceTimer: null,

      cobro: {
        metodo_pago: 'efectivo',
        destino_tipo: 'caja',
        destino_id: '',
        numero_comprobante: ''
      },
      cajas: [],
      bancos: [],

      notas: '',

      busquedaProducto: '',
      sugerenciasProducto: [],
      buscandoProducto: false,
      busquedaProductoTimeout: null,
      lineas: []
    }
  },

  async mounted() {
    await Promise.allSettled([this.fetchSriTypes(), this.cargarCajas(), this.cargarBancos()])
  },

  computed: {
    themeClass() {
      return themeManager.themeClass
    },
    claseAlerta() {
      return {
        error: 'alert-danger',
        advertencia: 'alert-warning',
        exito: 'alert-success'
      }[this.alerta.tipo] || 'alert-danger'
    },
    subtotal() {
      return this.lineas.reduce((acc, l) => acc + parseFloat(l.subtotal || 0), 0)
    },
    totalIva() {
      return this.lineas.reduce((acc, l) => acc + parseFloat(l.iva || 0), 0)
    },
    descuentoTotal() {
      return this.lineas.reduce((acc, l) => acc + parseFloat(l.descuento || 0) * parseFloat(l.cantidad || 0), 0)
    },
    total() {
      return this.subtotal + this.totalIva
    },
    lineasConStockExcedido() {
      return this.lineas.filter(l => l.producto_id && !l.es_servicio && l.cantidad > l.stock_disponible).length
    },
    gruposIva() {
      const grupos = {}
      for (const l of this.lineas) {
        const piva = parseFloat(l.porcentaje_iva || 0)
        const key  = piva.toFixed(2)
        if (!grupos[key]) grupos[key] = { porcentaje: piva, iva: 0 }
        grupos[key].iva += parseFloat(l.iva || 0)
      }
      return Object.values(grupos)
        .filter(g => g.porcentaje > 0 || g.iva > 0)
        .map(g => ({ ...g, iva: Math.round(g.iva * 100) / 100 }))
        .sort((a, b) => a.porcentaje - b.porcentaje)
    },
    destinosDisponibles() {
      if (this.cobro.destino_tipo === 'caja') {
        return this.cajas.map(c => ({ id: c.id, nombre: c.nombre_caja }))
      }
      return this.bancos.map(b => ({ id: b.id, nombre: b.nombre_banco }))
    }
  },

  methods: {
    async fetchSriTypes() {
      try {
        const { data } = await api.get('/tipos-identificacion-sri')
        this.sriTypes = (data.data || data || []).map(t => ({
          code: t.code ?? t.codigo ?? '',
          name: t.name ?? t.tipo_de_identificacion ?? ''
        })).filter(t => t.code !== '')
      } catch {
        this.sriTypes = [
          { code: '04', name: 'RUC' },
          { code: '05', name: 'Cédula' },
          { code: '06', name: 'Pasaporte' },
          { code: '07', name: 'Consumidor Final' },
          { code: '08', name: 'Identificación del Exterior' }
        ]
      }
    },

    async cargarCajas() {
      try {
        const { data } = await api.get('/cajas')
        this.cajas = (data?.cajas ?? data ?? []).filter(c => c.sesion_activa)
      } catch { /* silencioso */ }
    },
    async cargarBancos() {
      try {
        const { data } = await api.get('/bancos')
        this.bancos = (data?.bancos ?? data ?? []).filter(b => b.activo)
      } catch { /* silencioso */ }
    },

    // ── Cliente (mismo patrón que Facturación) ─────────────────
    onCambioTipoIdentificacion() {
      if (this.buyer.tipoIdentificacion === '07') {
        this.buyer.identificacion = '9999999999999'
        this.buyer.razonSocial    = 'CONSUMIDOR FINAL'
        this.buyer.telefono       = ''
        this.buyer.correo         = 'consumidorfinal@sri.gob.ec'
        this.buyer.direccion      = 'CONSUMIDOR FINAL'
        this.buyer.clientId       = null
      }
    },

    async buscarClienteAutomatico(tipoBusqueda) {
      const identificacion = this.buyer.identificacion?.trim()
      if (!identificacion || identificacion.length < 3) return

      this.buscandoCliente = true
      try {
        const { data } = await api.get('/clientes/buscar', { params: { q: identificacion, tipo: tipoBusqueda } })
        if (data.success && data.data) {
          const cliente = data.data
          this.buyer.identificacion = cliente.identification_number || identificacion
          this.buyer.razonSocial    = cliente.business_name || this.buyer.razonSocial
          this.buyer.direccion      = cliente.address || this.buyer.direccion
          this.buyer.correo         = cliente.email || this.buyer.correo
          this.buyer.telefono       = cliente.phone || this.buyer.telefono
          if (cliente.id) this.buyer.clientId = cliente.id
          if (cliente.tipo_identificacion) this.buyer.tipoIdentificacion = cliente.tipo_identificacion
        }
      } catch { /* si no se encuentra, se ingresa manualmente */ }
      finally { this.buscandoCliente = false }
    },

    buscarClientesMientrasEscribe() {
      const query = this.buyer.razonSocial?.trim()
      if (this.debounceTimer) clearTimeout(this.debounceTimer)

      if (!query || query.length < 2) {
        this.sugerenciasClientes = []
        this.mostrarSugerencias = false
        return
      }

      this.debounceTimer = setTimeout(async () => {
        this.buscandoCliente = true
        try {
          const { data } = await api.get('/clientes/autocompletar', { params: { q: query, limit: 8 } })
          if (data.success && data.data) {
            this.sugerenciasClientes = data.data
            this.mostrarSugerencias = true
            this.indiceSugerenciaActiva = -1
          } else {
            this.sugerenciasClientes = []
          }
        } catch {
          this.sugerenciasClientes = []
        } finally {
          this.buscandoCliente = false
        }
      }, 300)
    },

    seleccionarCliente(cliente) {
      this.buyer.clientId          = cliente.id
      this.buyer.identificacion    = cliente.identification_number || ''
      this.buyer.tipoIdentificacion = cliente.tipo_identificacion   || ''
      this.buyer.razonSocial       = cliente.business_name || ''
      this.buyer.correo            = cliente.email || ''
      this.buyer.telefono          = cliente.phone || ''
      this.buyer.direccion         = cliente.address || ''
      this.cerrarSugerencias()
    },

    navegarSugerencia(direccion) {
      if (this.sugerenciasClientes.length === 0) return
      this.indiceSugerenciaActiva += direccion
      if (this.indiceSugerenciaActiva < 0) this.indiceSugerenciaActiva = this.sugerenciasClientes.length - 1
      else if (this.indiceSugerenciaActiva >= this.sugerenciasClientes.length) this.indiceSugerenciaActiva = 0
    },

    seleccionarSugerenciaActiva() {
      if (this.indiceSugerenciaActiva >= 0 && this.sugerenciasClientes[this.indiceSugerenciaActiva]) {
        this.seleccionarCliente(this.sugerenciasClientes[this.indiceSugerenciaActiva])
      }
    },

    cerrarSugerencias() {
      this.sugerenciasClientes = []
      this.mostrarSugerencias = false
      this.indiceSugerenciaActiva = -1
    },

    cerrarSugerenciasConDelay() {
      setTimeout(() => { this.cerrarSugerencias() }, 200)
    },

    async resolverClienteId() {
      if (this.buyer.clientId) return this.buyer.clientId
      const payload = {
        identification_number: this.buyer.identificacion,
        business_name: this.buyer.razonSocial,
        address: this.buyer.direccion || null,
        phone: this.buyer.telefono || null,
        email: this.buyer.correo || 'consumidorfinal@sri.gob.ec',
        identification_type_sri: this.buyer.tipoIdentificacion
      }
      try {
        const { data } = await api.post('/clientes', payload)
        const newId = data?.id || data?.cliente_id
        if (!newId) throw new Error('El backend no retornó el ID del cliente creado.')
        this.buyer.clientId = newId
        return newId
      } catch (err) {
        const existingId = err.response?.data?.id || null
        if (err.response?.status === 409 && existingId) {
          this.buyer.clientId = existingId
          return existingId
        }
        throw err
      }
    },

    // ── Productos ─────────────────────────────────────────────
    esServicio(producto) {
      return (producto.tipo_producto || '').toLowerCase() === 'servicio'
    },

    buscarProductoInmediato() {
      clearTimeout(this.busquedaProductoTimeout)
      const q = this.busquedaProducto.trim()
      if (q.length < 1) { this.sugerenciasProducto = []; return }
      this.buscandoProducto = true
      api.get('/productos', { params: { filtros: JSON.stringify({ busqueda: q }), limit: 15, offset: 0 } })
        .then(res => {
          const lista = res.data?.data?.productos || []
          const qUp = q.toUpperCase()
          const exacto = lista.find(p =>
            (p.codigo_principal || '').toUpperCase() === qUp ||
            (p.codigo_auxiliar  || '').toUpperCase() === qUp
          )
          if (exacto) { this.agregarProducto(exacto) }
          else { this.sugerenciasProducto = lista }
        })
        .catch(() => {})
        .finally(() => { this.buscandoProducto = false })
    },

    onBusquedaProductoInput() {
      clearTimeout(this.busquedaProductoTimeout)
      const q = this.busquedaProducto.trim()
      if (q.length < 2) { this.sugerenciasProducto = []; return }
      this.buscandoProducto = true
      this.busquedaProductoTimeout = setTimeout(async () => {
        try {
          const res = await api.get('/productos', { params: { filtros: JSON.stringify({ busqueda: q }), limit: 15, offset: 0 } })
          this.sugerenciasProducto = res.data?.data?.productos || []
        } catch { /* silencioso */ }
        finally { this.buscandoProducto = false }
      }, 350)
    },

    async agregarProducto(producto) {
      const esServicio      = this.esServicio(producto)
      const stockDisponible = parseFloat(producto.stock_total || 0)

      this.sugerenciasProducto = []
      this.busquedaProducto    = ''

      if (!esServicio && stockDisponible <= 0) {
        this.alerta = { mensaje: `"${producto.nombre_producto}" no tiene stock disponible.`, tipo: 'error' }
        return
      }

      let precio = parseFloat(producto.precio_unitario || 0)
      let iva    = parseFloat(producto.tarifa_impuesto || 0)
      try {
        const resp = await api.get('/productos/' + producto.id + '/precios')
        const precios = resp.data?.data || []
        const precio1 = precios.find(x => x.tipo_precio === 'Precio1') || precios[0]
        if (precio1) precio = parseFloat(precio1.precio)
      } catch { /* usar precio_unitario del catálogo */ }

      const linea = {
        producto_id: producto.id,
        descripcion: producto.nombre_producto || '',
        codigo: producto.codigo_principal || '',
        es_servicio: esServicio,
        stock_disponible: stockDisponible,
        cantidad: 1,
        precio_unitario: precio,
        descuento: 0,
        porcentaje_iva: iva,
        subtotal: 0,
        iva: 0,
        total_detalle: 0
      }
      this.calcularLinea(linea)
      this.lineas.push(linea)
    },

    eliminarLinea(idx) {
      this.lineas.splice(idx, 1)
    },

    calcularLinea(linea) {
      const cant   = parseFloat(linea.cantidad        || 0)
      const precio = parseFloat(linea.precio_unitario || 0)
      const desc   = parseFloat(linea.descuento       || 0)
      const piva   = parseFloat(linea.porcentaje_iva  || 0)

      const sub = Math.max(0, (precio - desc) * cant)
      linea.subtotal      = parseFloat(sub.toFixed(4))
      linea.iva           = parseFloat((linea.subtotal * piva / 100).toFixed(4))
      linea.total_detalle = parseFloat((linea.subtotal + linea.iva).toFixed(4))
    },

    ajustarCantidad(linea) {
      linea.cantidad = Math.max(1, Math.floor(linea.cantidad || 1))
      if (linea.producto_id && !linea.es_servicio) {
        const maxStock = Math.floor(parseFloat(linea.stock_disponible || 0))
        if (linea.cantidad > maxStock) {
          linea.cantidad = maxStock
          this.alerta = { mensaje: `Stock máximo alcanzado para "${linea.descripcion}". Solo hay ${maxStock} unidad(es).`, tipo: 'advertencia' }
        }
      }
      this.calcularLinea(linea)
    },

    // ── Guardar ───────────────────────────────────────────────
    async guardar() {
      this.alerta.mensaje = ''

      if (!this.buyer.identificacion || !this.buyer.razonSocial) {
        this.alerta = { mensaje: 'Debes ingresar los datos del comprador (o seleccionar Consumidor Final).', tipo: 'error' }
        return
      }
      if (this.lineas.length === 0) {
        this.alerta = { mensaje: 'Agrega al menos un producto.', tipo: 'error' }
        return
      }
      if (!this.cobro.destino_id) {
        this.alerta = { mensaje: 'Selecciona el destino del cobro (caja o banco).', tipo: 'error' }
        return
      }
      const lineaSinStock = this.lineas.find(l => l.producto_id && !l.es_servicio && l.cantidad > l.stock_disponible)
      if (lineaSinStock) {
        this.alerta = { mensaje: `Stock insuficiente para "${lineaSinStock.descripcion}".`, tipo: 'error' }
        return
      }

      this.guardando = true
      try {
        const clienteId = await this.resolverClienteId()

        const detalles = this.lineas.map(l => ({
          producto_id: l.producto_id || null,
          descripcion: l.descripcion,
          codigo_principal: l.codigo || null,
          cantidad: l.cantidad,
          precio_unitario: l.precio_unitario,
          descuento: l.descuento,
          subtotal: l.subtotal,
          porcentaje_iva: l.porcentaje_iva,
          iva: l.iva,
          total_detalle: l.total_detalle
        }))

        const payload = {
          cliente_id: clienteId,
          notas: this.notas || null,
          descuento_total: this.descuentoTotal,
          detalles,
          cobro: { ...this.cobro }
        }

        await notaVentaService.crear(payload)
        notificaciones.success('Nota de venta creada y cobrada correctamente.')
        this.$router.push('/ventas/documentos-emitidos')
      } catch (e) {
        this.alerta = { mensaje: e?.response?.data?.error || 'Error al guardar la nota de venta.', tipo: 'error' }
      } finally {
        this.guardando = false
      }
    }
  }
}
</script>

<style scoped>
.sugerencias-lista {
  z-index: 1050;
  max-height: 220px;
  overflow-y: auto;
}
</style>
